/** Horizontal extent of a header cell, in scroll-content coordinates */
export interface ColumnEdge {
  left: number;
  right: number;
  pinned?: 'left' | 'right';
}

export type ScrollDirection = 'start' | 'end';

interface ScrollPageMetrics {
  /** Header cells in order, pinned ones included — they narrow the band the table scrolls in */
  columns: ColumnEdge[];
  /** Where the step starts from: the live scrollLeft, or the target of a step still animating */
  scrollLeft: number;
  clientWidth: number;
  scrollWidth: number;
}

/** Share of the visible band one step covers when no column boundary can move the table */
const FALLBACK_STEP = 0.85;
/** Fractional scroll positions: the tolerance of the edge flags, and of "the scroll reached its target" */
export const EPSILON_PX = 1;

/** The band the table scrolls in — what the pinned cells leave free — in scroll-content coordinates */
interface Band {
  /** Columns that scroll, in order */
  columns: ColumnEdge[];
  scrollLeft: number;
  start: number;
  end: number;
  leftPinned: number;
  maxScroll: number;
}

/**
 * Where one press of a scroll arrow takes the table.
 *
 * A page at a time, on column boundaries. Pinned cells stay put, so a page is the band they leave free:
 * - forward, the column cut off by the right-pinned cell becomes the first one in view;
 * - back, the column cut off on the left becomes the last one in view, then the table snaps to the nearest
 *   column start — the left edge never shows a cut column.
 *
 * A column wider than the band cannot be stepped past on boundaries, so the step falls back to a share of the band.
 */
export function scrollPageTarget(
  { columns, scrollLeft, clientWidth, scrollWidth }: ScrollPageMetrics,
  direction: ScrollDirection
): number {
  const pinnedWidth = (side: ColumnEdge['pinned']) =>
    columns.filter((column) => column.pinned === side).reduce((sum, column) => sum + column.right - column.left, 0);
  const leftPinned = pinnedWidth('left');
  const band: Band = {
    columns: columns.filter((column) => !column.pinned),
    scrollLeft,
    start: scrollLeft + leftPinned,
    end: scrollLeft + clientWidth - pinnedWidth('right'),
    leftPinned,
    maxScroll: scrollWidth - clientWidth
  };
  const clamp = (target: number) => Math.round(Math.min(Math.max(target, 0), band.maxScroll));
  const fallback = scrollLeft + (band.end - band.start) * FALLBACK_STEP * (direction === 'end' ? 1 : -1);

  if (!band.columns.length) return clamp(fallback);
  return clamp((direction === 'end' ? pageForward(band) : pageBack(band)) ?? fallback);
}

/** The column cut by the band's right edge to the left edge; null when boundaries cannot move the table forward */
function pageForward({ columns, scrollLeft, end, leftPinned, maxScroll }: Band): number | null {
  const cut = columns.find((column) => column.right > end + EPSILON_PX);
  const target = cut ? cut.left - leftPinned : maxScroll;
  return target > scrollLeft + EPSILON_PX ? target : null;
}

/** The column cut by the band's left edge to the right edge, snapped; null when boundaries cannot move the table back */
function pageBack({ columns, scrollLeft, start, end, leftPinned }: Band): number | null {
  const cut = [...columns].reverse().find((column) => column.left < start - EPSILON_PX);
  // Nothing cut on the left: only the content padding before the first column is out of view
  const target = cut ? snapToColumnStart(columns, scrollLeft + cut.right - end, leftPinned) : 0;
  return target < scrollLeft - EPSILON_PX ? target : null;
}

/**
 * The scroll position of the first column start at or after `position`. The first column itself means 0:
 * the content padding before it returns into view too.
 */
function snapToColumnStart(columns: ColumnEdge[], position: number, leftPinned: number): number {
  const snap = columns.find((column) => column.left - leftPinned >= position - EPSILON_PX);
  if (!snap) return position;
  return snap === columns[0] ? 0 : snap.left - leftPinned;
}
